import React, { useState, useEffect } from 'react';
import useQuery from '@/hooks/useQuery';

// Assuming these icons exist in your Heroicons setup
// If not, replace with appropriate icons from your icon library
const BellIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const RemindMeButton = ({ currentUserId, courseId }) => {
  const [saved, setSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const { getRequest, putpostRequest } = useQuery();

  useEffect(() => {
    if (!currentUserId) return;
    
    setIsLoading(true);
    getRequest(`/api/v1/courses/${courseId}/follow`, {}, (err, jsonData) => {
      setIsLoading(false);
      if (err) return;
      setSaved(jsonData.isFollowing);
    });
  }, [currentUserId, courseId]);

  const toggleSave = () => {
    if (isLoading) return;
    
    setIsLoading(true);
    putpostRequest(`/api/v1/courses/${courseId}/follow`, 'POST', {}, (err, jsonData) => {
      setIsLoading(false);
      if (err) return;
      setSaved(jsonData.isFollowing);
    });
  };

  // Button styles based on state
  const getButtonStyles = () => {
    const baseStyles = `
      relative 
      w-full 
      flex 
      items-center 
      justify-center 
      px-4 
      py-3 
      font-medium 
      rounded-lg 
      shadow-sm 
      transition-all 
      duration-300 
      ease-in-out 
      focus:outline-none 
      focus:ring-2 
      focus:ring-offset-2 
    `;

    if (isLoading) {
      return `${baseStyles} bg-gray-50 text-gray-400 border border-gray-200 cursor-wait`;
    }

    if (saved) {
      return `${baseStyles} ${
        isHovering 
          ? 'bg-white text-gray-700 border border-gray-300' 
          : 'bg-green-50 dark:bg-green-950/30 text-green-600 border border-green-200'
      } focus:ring-green-500`;
    }

    return `${baseStyles} bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-clcpurple hover:text-clcpurple focus:ring-clcpurple`;
  };

  const tooltip = saved 
    ? "You'll be notified when this course is available" 
    : "Get notified when this course is available";

  return (
    <div className="relative group" title={tooltip}>
      <button
        onClick={toggleSave}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        disabled={isLoading}
        className={getButtonStyles()}
        aria-label={saved ? "Remove reminder" : "Set reminder"}
      >
        {isLoading ? (
          <div className="flex items-center space-x-2">
            <SpinnerIcon />
            <span>Processing...</span>
          </div>
        ) : saved ? (
          <div className="flex items-center space-x-2 transition-all duration-300">
            {isHovering ? (
              <>
                <BellIcon />
                <span>Remove reminder</span>
              </>
            ) : (
              <>
                <CheckIcon />
                <span>Reminder set</span>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <BellIcon />
            <span>Remind me</span>
          </div>
        )}
      </button>
      {/* Optional pulsing dot indicator for "saved" state */}
      {saved && !isHovering && (
        <span className="absolute top-0 right-0 block h-2.5 w-2.5 -mt-1 -mr-1">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
        </span>
      )}
    </div>
  );
};

export default RemindMeButton;
